import { useTranslation } from "react-i18next";
import useWindowWidth from "../../Hooks/useWindowWidth";
import VideoContent from "../../Components/Home/VideoContent";

// Asset Imports (Grouped for clarity)
import sanitation_image from "../../Assets/Images/sanitation_image.png";
import gif1 from "../../Assets/Images/background_gif.gif";
import privacy_policy from "../../Assets/Images/privacy-policy.gif";
import personnel from "../../Assets/Images/personnel.gif";
import pie_chart from "../../Assets/Images/pie_chart.gif";
import problem_solving from "../../Assets/Images/problem_solving.gif";
import knowledge from "../../Assets/Images/knowledge.gif";
import feedback from "../../Assets/Images/feedback.gif";
import maps from "../../Assets/Images/maps.gif";
import moodboard from "../../Assets/Images/moodboard.gif";
import goals from "../../Assets/Images/goals.gif";
import box_image from "../../Assets/Images/box_image.gif";
import corporate_culture from "../../Assets/Images/corporate_culture.gif";
import government from "../../Assets/Images/government.gif";
import business_development from "../../Assets/Images/business_development.gif";
import toilet_bowl from "../../Assets/Images/toilet_bowl.gif";
import architecture from "../../Assets/Images/architecture.gif";
import nursing from "../../Assets/Images/nursing_technician.gif";

import aboutnsdthumb from "../../Assets/Images/aboutnsdthumb.png";

import city_profile_img from "../../Assets/Images/city_profile_compare_page.png";
import add_user_page_img from "../../Assets/Images/add-user-page-img.png";
import data_collection__kii_img from "../../Assets/Images/data-collection-from-kii-img.png";
import overview_img from "../../Assets/Images/overview-img.png";
import institutional_capacity_img from "../../Assets/Images/instituitional-capacity-img.png";
import kii_questionnaire_page_img from "../../Assets/Images/kii-questionnaire-page-img.png";
import location_modal from "../../Assets/Images/location_modal.png";
import survey_dashboard from "../../Assets/Images/survey_dashboard.png";
import wash_img from "../../Assets/Images/wash-img.png";
import logos from "../../Assets/Images/about_page_logos_prev_ui.png";

import {
  SVG_BRANCH_ICON,
  SVG_CHEVRON_DOWN,
  SVG_EDIT_ICON,
  SVG_INFO,
  SVG_INTEGRATION,
  SVG_LIST_ICON,
  SVG_MONITOR_ICON,
  SVG_STACKHOLDERS,
  SVG_VIDEO_PLAYER,
} from "../../Assets/SVG/svg_util";

// --- Data Definitions ---

const navigationCards = [
  { icon: <SVG_VIDEO_PLAYER />, label: "Explainer Video", targetId: "video" },
  { icon: <SVG_MONITOR_ICON />, label: "What is NSD", targetId: "what-is-nsd" },
  {
    icon: <SVG_STACKHOLDERS />,
    label: "Stakeholders and Use-Cases",
    targetId: "stakeholders",
  },
  { icon: <SVG_INFO />, label: "Background of NSD", targetId: "background" },
  {
    icon: <SVG_EDIT_ICON />,
    label: "Assessment Phase",
    targetId: "assessment",
  },
  {
    icon: <SVG_BRANCH_ICON />,
    label: "Enhancements Informed",
    targetId: "enhancements",
  },
  { icon: <SVG_INTEGRATION />, label: "Integration", targetId: "integration" },
  {
    icon: <SVG_LIST_ICON />,
    label: "Detailed Enhancements",
    targetId: "detailed-enhancements",
  },
];

const stakeholders = [
  {
    img: privacy_policy,
    title: "Regulators and Policymakers",
    desc: "Government officials tasked with developing sanitation policies and ensuring compliance (e.g., DPHE, LGD).",
  },
  {
    img: architecture,
    title: "City Planners",
    desc: "Professionals focused on designing and managing sanitation infrastructure in urban areas (e.g., DNCC).",
  },
  {
    img: nursing,
    title: "Community Health Workers",
    desc: "Field personnel dedicated to enhancing sanitation conditions and monitoring health outcomes.",
  },
  {
    img: toilet_bowl,
    title: "Sanitation Service Providers",
    desc: "Organizations responsible for providing sanitation services and maintaining public infrastructure.",
  },
  {
    img: business_development,
    title: "Donors",
    desc: "Funding agencies who assess the impact and efficiency of sanitation initiatives to allocate resources effectively.",
  },
  {
    img: knowledge,
    title: "Researchers and Analysts",
    desc: "Academics and consultants who investigate sanitation trends and evaluate program effectiveness.",
  },
  {
    img: government,
    title: "Local Government Authorities",
    desc: "Municipal bodies supervising sanitation services at the local level.",
  },
  {
    img: corporate_culture,
    title: "Citizens",
    desc: "Community members who use the dashboard to report issues and engage in decision-making.",
  },
];

const backgroundCards = [
  {
    title: "2020",
    description: "National Sanitation Dashboard (NSD) was inaugurated in 2020.",
  },
  {
    title: "61 Towns",
    description:
      "Data was collected from 53 district paurashavas and 8 city corporations.",
  },
  {
    title: "Feasibility Study",
    description:
      "Included household surveys, FGD, KII, and Physical Feature Surveys.",
  },
];

const assessmentItems = [
  "Consultation with Officials from Different Stakeholders",
  "Studying the Sanitation Data Ecosystem between different entities",
  "Mapping the current Data Collection Processes and Administrative Challenges",
  "Preparation of Data Production vs Data Requirements Map of different ministries/agencies/stakeholders",
  "Workshop with DPHE and other relevant agencies, Municipal Authorities and Sanitation Partners",
  "Researching Integration Opportunities with SDG Tracker and IMIS",
  "Assisting a2i in Steering Committee Formulation",
  "Preparing Action Plan for the upscaling National Sanitation Dashboard",
];
const informedEnhancementsItems = [
  "Data updating mechanism for effective trend data analysis",
  "Expansion of SDG and CWIS Indicators",
  "Broader Geographical Coverage",
  "Integration with the national and sub-national data systems (SDG Tracker and IMIS)",
  "Incorporation of more dynamic maps and visualisations",
  "Export and download options (data, reports, charts, graphs, etc)",
  "Incorporation of sanitation survey data",
];

const detailedEnhancements = [
  { title: "Multilevel Administrative Configuration", img: personnel },
  { title: "Survey Management and Monitoring", img: moodboard },
  {
    title: "Integration with Sub-National and National Data Systems",
    img: problem_solving,
  },
  { title: "Customizable Indicators and Metrics", img: feedback },
  { title: "Interactive and Exportable Charts and Reports", img: pie_chart },
  { title: "Customizable Map", img: maps },
];

const majorEnhancements = [
  {
    title: "Multilevel Administrative Configuration",
    desc: "will enhance security and usability by implementing role-based access control with user-specific interfaces and permissions.",
  },
  {
    title: "Survey Management and Monitoring",
    desc: "capabilities will allow real-time tracking of survey progress, resource allocation, and data visualization to improve efficiency and data quality.",
  },
  {
    title: "Integration with Data Systems",
    desc: "includes the Integrated Municipal Information System (IMIS), SDG Tracker, and National Dashboard Framework (NDF) for seamless data exchange.",
  },
  {
    title: "Customizable Indicators and Metrics",
    desc: "will allow users to select, analyze, and compare specific sanitation indicators and service outcomes between cities across years.",
  },
  {
    title: "Interactive Charts and Reports",
    desc: "will enable dynamic data exploration with options to zoom, filter, and export visualizations for offline analysis.",
  },
  {
    title: "Customizable Maps",
    desc: "will provide interactive geospatial tools to analyze sanitation coverage, water quality, and infrastructure gaps for localized planning.",
  },
];

const minorEnhancements = [
  {
    title: "Multilingual User Interface",
    desc: "will improve accessibility with a one-click language switcher for English and Bengali.",
  },
  {
    title: "Information Tooltip",
    desc: "will be added to offer contextual explanations of technical terms, ensuring better comprehension for all users.",
  },
  {
    title: "City Profile Data from Multiple Sources",
    desc: "will consolidate datasets from DPHE, IMIS, MICS, and other sources for a comprehensive view of city-specific scenarios.",
  },
];

// --- Reusable UI Components ---

const SectionTitle = ({ children }) => (
  <p className="text-2xl md:text-3xl lg:text-4xl border-s-8 border-[#2F4B7C] pl-5 font-medium text-left">
    {children}
  </p>
);

const NavigationGrid = () => {
  const width = useWindowWidth();
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4  md:gap-y-5 lg:gap-y-10 my-10 md:my-12 lg:my-14 font-poppins font-light">
      {navigationCards.map((item, index) => {
        const isLastInRow =
          (width >= 1024 && (index + 1) % 4 === 0) ||
          (width >= 640 && width < 1024 && (index + 1) % 3 === 0) ||
          (width < 640 && (index + 1) % 2 === 0);

        return (
          <div
            key={index}
            onClick={() => {
              const section = document.getElementById(item.targetId);
              if (section) {
                section.scrollIntoView({ behavior: "smooth" });
              }
            }}
            className="flex flex-col items-center justify-center relative w-full"
          >
            <div className="group w-5/6 py-6 px-4 flex flex-col items-center justify-center transition-all duration-300 hover:scale-105 hover:shadow-[0_0_15px_rgba(236,72,153,0.5)] hover:rounded-2xl cursor-pointer">
              <p className="transition-all duration-300 text-black group-hover:text-[#2F4B7C] group-hover:scale-110">
                {item.icon}
              </p>
              <p className="mt-2 text-gray-500 transition-colors duration-300 group-hover:text-[#2F4B7C]">
                {item.label}
              </p>
              <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <SVG_CHEVRON_DOWN />
              </span>
            </div>
            {!isLastInRow && (
              <div className="absolute right-0 top-1/2 -translate-y-1/2 h-16 w-px bg-gray-300"></div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const StakeholderCard = ({ img, title, desc }) => (
  <div className="flex flex-col items-center p-4 text-center w-full sm:w-5/12 lg:w-1/4">
    <div className="flex justify-center items-center mb-4 h-24">
      <img src={img} alt={title} className="max-h-full w-auto object-contain" />
    </div>
    <p className="font-semibold text-xl mb-2 lg:w-64">{title}</p>
    <p className="leading-6 about-section-text">{desc}</p>
  </div>
);

const EnhancementDetail = ({ title, desc }) => (
  <p>
    <span className="font-medium">{title}</span> {desc}
  </p>
);

const BulletListSection = ({ title, items, imageSrc, children }) => (
  <div>
    <SectionTitle>{title}</SectionTitle>
    <div className="flex justify-between mt-4">
      <div className="w-full lg:w-10/12">
        {children}
        <ul className="about-section-text pl-8">
          {items.map((item, index) => (
            <li key={index} className="flex items-center">
              <span className="w-1 h-1 mr-3 bg-gray-700 rounded-full"></span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
      {imageSrc && (
        <div className="hidden lg:flex justify-center items-center w-1/4 h-24">
          <img
            src={imageSrc}
            alt=""
            className="max-h-24 w-auto object-contain"
          />
        </div>
      )}
    </div>
  </div>
);

// --- Main About Component ---

const About = () => {
  const { t } = useTranslation();

  return (
    <div className="text-[#4C6187] text-base font-roboto space-y-10 md:space-y-16 lg:space-y-20 ">
      <div className="about-section-width xl:pl-20">
        {/* Header Section */}
        <div className="text-left mt-5">
          <p className="opacity-70 mb-5 text-base font-medium">
            {t("nav.about")}
          </p>
          <b className="text-3xl md:text-4xl  lg:text-[56px] text-[#2F4B7C]">
            {t("about.title")}
          </b>
          <div className="w-full flex items-center h-auto justify-center my-8">
            <img
              src={sanitation_image}
              alt="Sanitation project overview"
              className="max-w-full object-contain rounded-3xl"
            />
          </div>
          <p className="text-xl  md:text-2xl lg:text-3xl  leading[1.5rem] md:leading-[2rem]  xl:leading-[2.5rem] font-medium text-justify">
            {t("about.background_paragraph")}
          </p>
        </div>

        <NavigationGrid />
      </div>

     {/* Video Demonstration */}
      <div id="video" className="xl:pl-20">
        <SectionTitle>
          Video Demonstration of the National Sanitation Dashboard
        </SectionTitle>
        <div className="about-section-width mt-5">
          <div className="mt-3 mb-2 p-1 border-2 border-[#e3e3e3] rounded-lg">
            <VideoContent
              videoUrl="https://youtu.be/QbqfhtMnsos"
              thumbnailSrc={aboutnsdthumb}
              title="NSD demo video"
            />
          </div>
          {/* <p className="mx-auto md:pl-8 text-[#555555] text-start  text-sm md:text-lg lg:text-xl opacity-80">
            [Please note: A screenshot has been used in this design in place of
            the video to enhance accessibility]
          </p> */}
        </div>
      </div>

      {/* What is NSD Section */}
      <div id="what-is-nsd" className="flex w-full xl:pl-20">
        <div className="about-section-width">
          <SectionTitle>What is National Sanitation Dashboard</SectionTitle>
          <div className="text-justify md:pl-8 space-y-5 about-section-text leading-7 mt-5">
            <p>
              The National Sanitation Dashboard is a comprehensive digital
              platform designed to enhance sanitation management and drive
              informed decision-making. It supports safe waste management
              practices, facilitates efficient planning, and streamlines survey
              management and monitoring. By integrating crucial data, advanced
              analytics, and user-friendly interfaces, the dashboard empowers
              stakeholders with actionable insights.
            </p>
            <p>
              The Dashboard aims to enhance sanitation management by offering a
              platform for monitoring sanitation status against international
              and local indicators. It facilitates the comparison of city
              profiles, strengthens coordination among stakeholders, and drives
              collaborative efforts toward achieving goals outlined in the SDGs
              and CWIS frameworks.
            </p>
            <p>
              Under the flagship program of the Government of Bangladesh, the
              Strengthening Public Data System for Sanitation in Bangladesh
              (SPDSSB), the redesign and upscaling of the NSD were undertaken.
              This initiative, implemented by Tiller with technical inputs from
              Athena Infonomics and ITN-BUET, incorporated a comprehensive list
              of sanitation indicators to strengthen evidence-based
              decision-making.
            </p>
          </div>
        </div>
        <div className="hidden xl:flex justify-center items-center w-1/4">
          <img
            src={gif1}
            alt="Sanitation Dashboard GIF"
            className="max-h-44 w-auto object-contain"
          />
        </div>
      </div>

      {/* Stakeholders Section */}
      <div id="stakeholders" className="about-section-width xl:pl-20">
        <SectionTitle>Stakeholders</SectionTitle>
        <div className="flex flex-wrap justify-center md:gap-10 lg:gap-20 my-10 md:my-12 lg:my-14">
          {stakeholders.map((stakeholder, index) => (
            <StakeholderCard key={index} {...stakeholder} />
          ))}
        </div>
      </div>

      {/* Use Cases Section */}
      <div className="xl:w-10/12 text-left xl:pl-20 ">
        <SectionTitle>Use-Cases</SectionTitle>
        <div className="md:px-8 w-full mt-8">
          <h3 className="about-use-cases-section-title">City Comparison</h3>
          <div className="space-y-10  md:space-y-12 lg:space-y-16 ">
            {" "}
            {/* City Comparison */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 ">
              <div className="flex flex-col">
                <p className="about-section-text leading-8 mb-6">
                  Two cities can be compared based on SDG sanitation indicators
                  or Institutional Capacity, helping decision-making regarding
                  resource allocation.
                </p>
                <img
                  src={city_profile_img}
                  alt="City profile comparison"
                  className="flex-1  w-full h-full object-fill border shadow-sm"
                />
              </div>

              <div className="flex flex-col space-y-6">
                <p className="about-section-text leading-8">
                  Two cities can be compared based on SDG sanitation indicators
                  or Institutional Capacity, helping decision-making regarding
                  resource allocation.
                </p>
                <img
                  src={overview_img}
                  alt="Overview metrics"
                  className="w-full h-auto object-cover border shadow-sm"
                />
                <img
                  src={institutional_capacity_img}
                  alt="Institutional capacity metrics"
                  className="w-full h-auto object-cover border shadow-sm"
                />
              </div>
            </div>
            {/* Role Based Access Control */}
            <div>
              <h3 className="about-use-cases-section-title">
                Role Based Access Control
              </h3>
              <p className="about-section-text leading-8 mb-6 lg:w-1/2">
                Role Based Access Control manages permissions to determine who
                has access at both DPHE and municipality levels.
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
                <img
                  src={add_user_page_img}
                  alt="Add user page"
                  className="w-full h-auto object-cover border shadow-sm"
                />
                <img
                  src={location_modal}
                  alt="Users page"
                  className="w-full h-auto object-cover border shadow-sm"
                />
              </div>
            </div>
            {/* Survey Management */}
            <div>
              <h3 className="about-use-cases-section-title">
                Survey Management and Monitoring
              </h3>
              <p className="about-section-text leading-8 mb-6 lg:w-1/2">
                A centralized platform for the comprehensive management and
                monitoring of surveys.
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div className="space-y-10">
                  <img
                    src={survey_dashboard}
                    alt="KII Questionnaire"
                    className="w-full h-auto object-cover border shadow-sm"
                  />
                  <img
                    src={kii_questionnaire_page_img}
                    alt="KII Questionnaire"
                    className="w-full h-auto object-cover border shadow-sm"
                  />
                </div>
                <img
                  src={data_collection__kii_img}
                  alt="Data collection from KII"
                  className="w-full h-auto object-cover border shadow-sm"
                />
              </div>
            </div>
            <p className="text-center">and, many more...</p>
          </div>
        </div>
      </div>

      {/* Background Section */}
      <div
        id="background"
        className="w-full bg-[#F2F2F2] py-20 relative xl:pl-20"
      >
        <SectionTitle>
          Background of the National Sanitation Dashboard
        </SectionTitle>
        <div className="hidden xl:flex justify-end w-full absolute top-0 right-0 h-auto ">
          <img src={box_image} alt="" className="w-1/6 h-auto object-contain" />
        </div>

        <div className="py-5 lg:py-10 pl-5 w-full xl:w-10/12 lg:my-10">
          <div className="mx-auto grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 px-4">
            {backgroundCards.map((card, index) => (
              <div
                key={index}
                className="bg-[#2F4B7C] text-white text-start rounded-lg shadow-md p-12"
              >
                <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold mb-3">
                  {card.title}
                </h2>
                <p className="leading-relaxed about-section-text">
                  {card.description}
                </p>
              </div>
            ))}
          </div>
        </div>
        <div className="text-justify px-8 w-full xl:w-10/12 space-y-4  md:space-y-6  lg:space-y-8  about-section-text">
          <p>
            {/* In 2020, DPHE spearheaded a project for implementing Solid Waste and
            Fecal Sludge Management Systems in 53 Paurashavas and 8 City
            Corporations. */}
            Bangladesh has made remarkable progress in eliminating open
            defecation. However, this achievement has led to a new{" "}
            <b>“second-generation”</b> sanitation challenge—ensuring the safe
            management of fecal sludge collected from pit latrines and septic
            tanks. As on-site sanitation solutions expand, the need for
            sustainable and inclusive fecal sludge management (FSM) and solid
            waste management (SWM) has become increasingly critical, especially
            in urban areas. Introducing effective and feasible technologies is
            essential to ensure that city corporations and district towns can
            provide at least basic services to their populations in a
            sustainable manner.
          </p>
          <p>
            {/* <span className="font-semibold">Tiller</span> worked as the
            consultant on a study for "Establishing IT and GIS Based Smart Solid
            Waste & Fecal Sludge Management Systems." The results formed a basis
            for investment projects and sustainable operational plans. */}
            To understand the scale of the challenge, a rapid assessment across
            8 City Corporations and 53 District-level Paurashavas became
            indispensable. This assessment focused on determining service area
            and population coverage, estimating service demand, evaluating
            technological options for FSM and SWM chains, assessing public
            acceptance and willingness to pay, and identifying investment and
            operational requirements. Addressing these issues is urgent,
            especially in fast-growing cities and large towns where the problem
            is most acute.
          </p>
          <p>
        To respond to these growing concerns, the Department of Public Health Engineering (DPHE) initiated a Technical Assistance Project titled <b>“Feasibility for Implementing Solid Waste and Fecal Sludge Management Systems in 53 District-Level Paurashavas and 8 City Corporations.” </b>  This project was carried out by the dynamic leadership of <b>Mr. Shishir Kumar Biswas, Executive Engineer, DPHE.</b> The Bill & Melinda Gates Foundation (BMGF) supported this initiative with both technical and financial assistance. Under this project, DPHE appointed <b>DE Consultants Limited (DevCon), in association with Tiller,</b> to conduct the study <b>“Rapid Assessment for Establishing IT and GIS-Based Smart Solid Waste & Fecal Sludge Management Systems.”</b> The study results serve as a foundation for future government and development-partner investment projects, along with sustainable plans for operation and maintenance by city authorities.
          </p>
          <p>
            The data visualized in this dashboard were collected through field
            surveys conducted in 2020. Primary data came from{" "}
            <b>household surveys</b>, while secondary data were gathered from
            local government bodies and relevant institutions. Additionally,{" "}
            <b>
              Key Informant Interviews (KIIs) and Focus Group Discussions (FGDs)
            </b>{" "}
            were conducted to enrich and validate the findings. The household
            surveys in each town used a stratified random sampling method with a{" "}
            <b>95% confidence level and a 5% margin of error</b>, ensuring
            reliable and representative results.
          </p>
        </div>
      </div>

      {/* Scoping Study & Enhancements */}
      <div className="space-y-10 md:spce-y-12 lg:space-y-16 text-left w-full xl:pl-20">
        <div id="assessment">
          <BulletListSection
            title="Assessment Phase (Scoping Study of the National Sanitation Dashboard)"
            items={assessmentItems}
            imageSrc={knowledge}
          >
            <p className="about-section-text leading-relaxed text-start pl-8">
              A scoping study of the NSD was conducted in 2023 by Tiller, which
              included the following activities:
            </p>
          </BulletListSection>
        </div>

        <div id="enhancements">
          <BulletListSection
            title="Enhancements Informed by the Scoping Study"
            items={informedEnhancementsItems}
            imageSrc={goals}
          >
            <p className="text-lg leading-relaxed pl-8">
              The following enhancements were informed by the Scoping Study:
            </p>
          </BulletListSection>
        </div>

        <div id="integration">
          <SectionTitle>
            Integration with Sub-National and National Data Systems
          </SectionTitle>
          <div className="flex justify-between mt-4">
            <p className="w-full lg:w-3/4 text-justify about-section-text leading-8 pl-8">
              The National Sanitation Dashboard will be seamlessly integrated
              with Sub-National Data System: Integrated Municipal Information
              System (IMIS) and National Data System: National Dashboard
              Framework (NDF), creating a unified and efficient data ecosystem.
              This integration will enable the smooth flow of data across
              different administrative levels, providing a comprehensive view of
              sanitation progress nationwide. By fostering better coordination
              between national and local stakeholders, the dashboard will
              enhance efforts to address sanitation challenges effectively,
              ensuring aligned and impactful strategies for improved sanitation
              outcomes.
            </p>
            <div className="hidden lg:flex justify-center items-center w-1/4">
              <img
                src={problem_solving}
                alt=""
                className="max-h-24 w-auto object-contain mx-auto"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Enhancements Grid */}
      <div id="detailed-enhancements" className="xl:pl-20">
        <div className="text-left my-10 lg:my-16 about-section-width ">
          <SectionTitle>
            Detailed Enhancements Informed by the Scoping Study
          </SectionTitle>
          <div className="p-6 md:p-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5  lg:gap-10 xl:gap-20 my-5">
              {detailedEnhancements.map((item, index) => (
                <div key={index} className="flex flex-col items-center ">
                  <div className="flex justify-center items-center  h-24">
                    <img
                      src={item.img}
                      alt={item.title}
                      className="max-h-full w-auto object-contain"
                    />
                  </div>
                  <h3 className="text-base md:text-lg lg:text-xl font-medium text-center w-3/4 mx-auto">
                    {item.title}
                  </h3>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Major/Minor Enhancement Details */}
        <div className="text-left space-y-12 about-section-width pl-8">
          <section>
            <h2 className="text-2xl font-semibold mb-6">Major Enhancements</h2>
            <div className="space-y-4 about-section-text leading-relaxed">
              {majorEnhancements.map((item, index) => (
                <EnhancementDetail key={index} {...item} />
              ))}
            </div>
          </section>
          <section>
            <h2 className="text-2xl font-semibold mb-6">Minor Enhancements</h2>
            <div className="space-y-4 about-section-text  leading-relaxed">
              {minorEnhancements.map((item, index) => (
                <EnhancementDetail key={index} {...item} />
              ))}
            </div>
          </section>
        </div>
      </div>

      {/* Footer Section */}
      <div className="mx-auto text-center">
        <p className="text-[#369376]  text-xl md:text-3xl lg:text-5xl font-semibold mb-8">
          Towards a Better WASH
        </p>
        <div className="w-3/4 mx-auto flex justify-center">
          <img
            src={wash_img}
            alt="WASH illustration"
            className="rounded-full object-contain"
          />
        </div>
      </div>

      {/* Logos */}
      <div className="h-16 w-full px-4">
        <img
          src={logos}
          className="h-full w-full object-contain"
          alt="Partner logos"
        />
      </div>
    </div>
  );
};

export default About;
